import { AssignmentType, IProduct, ISalesOrderLineAssignment, Maybe } from '../../graphql/schema/p4types';
import { IEditableDataGridColumn, IFunctionKeyFormatterOptions } from '../../@types/editableDataGridTypes';
import Formatters from '../grid-addons/formatters';
import { IFormatterProps } from '../../@types/workspaceTypes';
import { Link } from 'react-router-dom';
import React from 'react';
import NumericFormatter from '../grid-addons/formatters/NumericFormatter';
import TextFieldFormatter from '../grid-addons/formatters/TextFieldFormatter';
import getIn from '../connectedForm/utils/getIn';
import SalesOrder from '../../graphql/entities/sales/SalesOrder';
import { useLocation } from 'react-router';

/**
 * Assignments can be of a variety of types, so this is a special link Formatter handling the various types.
 */
const AssignmentLinkFormatter = (formatterProps: IFormatterProps<ISalesOrderLineAssignment>) => {
  const assignment = formatterProps.dependentValues.rowData;
  const assignmentType = assignment.assignmentType;
  const location = useLocation();

  if (formatterProps.column.key === 'salesOrderLine.salesOrder.id') {
    //If viewing from the Purchase Order, key will be for the sales order id, so we can provide a link to the Sales Order.
    const salesOrder = assignment.salesOrderLine?.salesOrder;
    return (
      <Link to={`${SalesOrder.formUrl}${salesOrder?.id}`} rel={'noreferrer noopener'} title={'View Sales Order'}>
        <div className="textlink" data-testid={`datagrid-link-icon-${formatterProps.rowIdx}`}>
          SO Number {salesOrder?.orderNumber}
        </div>
      </Link>
    );
  } else if (assignmentType === AssignmentType.PurchaseOrder) {
    //If viewing from the Sales Order, and the assignment type is PURCHASE_ORDER, show a link to the Purchase Order.
    const purchaseOrder = assignment?.purchaseOrderLine?.purchaseOrder;

    return (
      <Link
        to={`/Purchasing/Order/Details/${purchaseOrder?.id}`}
        rel={'noreferrer noopener'}
        title={'View Purchase Order'}
      >
        <div className="textlink" data-testid={`datagrid-link-icon-${formatterProps.rowIdx}`}>
          PO Number {purchaseOrder?.orderNumber}
        </div>
      </Link>
    );
  } else if (assignmentType === AssignmentType.Shortage) {
    //If viewing from the Sales Order, and the assignment type is SHORTAGE, show a link to the Purchase Requirement Detail.
    const purchaseRequirementDetail = assignment.purchaseRequirementDetail;
    return (
      <Link
        to={{
          pathname: `/Purchasing/Shortages/ShortageDetails/Details/${purchaseRequirementDetail?.id}`,
          state: {
            previousLocation: location.pathname || 'back',
          },
        }}
        rel={'noreferrer noopener'}
        title={'View Purchase Requirement'}
      >
        <div className="textlink" data-testid={`datagrid-link-icon-${formatterProps.rowIdx}`}>
          Purchase Requirement {purchaseRequirementDetail?.id}
        </div>
      </Link>
    );
  } else if (assignmentType === AssignmentType.Assembly) {
    const assemblyRequirementDetail = assignment.assemblyRequirementDetail;
    return (
      <Link
        to={{
          pathname: `/Products/Assembly/AssemblyShortages/Details/${assemblyRequirementDetail?.id}`,
          state: {
            previousLocation: location.pathname || 'back',
          },
        }}
        rel={'noreferrer noopener'}
        title={'View Assembly Shortage'}
      >
        <div className="textlink" data-testid={`datagrid-link-icon-${formatterProps.rowIdx}`}>
          Assembly Shortage {assemblyRequirementDetail?.id}
        </div>
      </Link>
    );
  }
  return <div>Not yet supported</div>;
};

/**
 * A wrapper around any arbitrary Formatter that ignores the 'key' property and instead determines a key fia the 'keyFunction' option.
 * (Such that if a grid row has multiple implementations, we can specify the keys as appropriate within re-implementing whole formatters each time.)
 */
const FunctionKeyFormatter = (props: IFormatterProps<unknown>) => {
  const { column, ...rest } = props;
  const options = props.dependentValues.options as IFunctionKeyFormatterOptions;
  const { keyFunction, formatter: Formatter, formatterOptions, ...other } = options;
  const key = keyFunction?.(props.dependentValues.rowData) ?? 'undefined';

  return (
    <Formatter
      {...rest}
      column={{ ...column, key: key }}
      dependentValues={{ ...props.dependentValues, options: { ...(options.formatterOptions ?? {}), ...other } }}
      value={getIn(props.dependentValues.rowData, key)}
    />
  );
};

export const useAssemblyAssignmentColumns = (type: 'SALES' | 'ASSEMBLY' | 'PURCHASE') => {
  const salesOrAssemblyColumns: Array<IEditableDataGridColumn> = [
    {
      name: `Assembly Order Number`,
      key: `assembly.orderNumber`,
      editable: false,
      resizable: true,
    },
    {
      name: 'Assembly Product',
      key: 'assembly.product.reference',
      editable: false,
      resizable: true,
    },
    {
      name: 'Assembly Qty',
      key: 'assembly.totalQuantity',
      editable: false,
      resizable: true,
      formatter: 'numeric',
    },
    {
      name: 'Assigned',
      key: 'assigned',
      editable: false,
      resizable: true,
      formatter: 'numeric',
    },
  ];

  const purchaseColumns: Array<IEditableDataGridColumn> = [
    {
      name: `Assembly Order Number`,
      key: `parentAssembly.orderNumber`,
      editable: false,
      resizable: true,
    },
    {
      name: 'Assembly Product',
      key: 'parentAssembly.product.reference',
      editable: false,
      resizable: true,
    },
    {
      name: 'Assembly Qty',
      key: 'parentAssembly.totalQuantity',
      editable: false,
      resizable: true,
      formatter: 'numeric',
    },
    {
      name: 'Assigned',
      key: 'assigned',
      editable: false,
      resizable: true,
      formatter: 'numeric',
    },
  ];

  switch (type) {
    case 'ASSEMBLY':
      return salesOrAssemblyColumns;
    case 'SALES':
      return salesOrAssemblyColumns;
    case 'PURCHASE':
      return purchaseColumns;
  }
};

export const useAssignmentColumns = (type: 'SALES' | 'PURCHASE', product: Maybe<IProduct>) => {
  let labelName: string;
  let lineKey: string;
  let orderKey: string;
  if (type === 'SALES') {
    labelName = 'Purchase';
    orderKey = 'purchaseOrderLine.purchaseOrder';
    lineKey = 'purchaseOrderLine';
  } else {
    labelName = 'Sales';
    orderKey = 'salesOrderLine.salesOrder';
    lineKey = 'salesOrderLine';
  }

  const columns: Array<IEditableDataGridColumn> = [
    {
      name: `${labelName} Order Number`,
      key: `${orderKey}.id`,
      editable: false,
      resizable: true,
      formatter: AssignmentLinkFormatter,
    },
  ];

  if (type === 'SALES') {
    columns.push({
      name: 'Product',
      key: `${lineKey}.product.reference`,
      editable: false,
      resizable: true,
      formatter: FunctionKeyFormatter,
      options: {
        formatter: TextFieldFormatter,
        keyFunction: (rowData: ISalesOrderLineAssignment) =>
          rowData.assignmentType === AssignmentType.PurchaseOrder
            ? `${lineKey}.product.reference`
            : 'purchaseRequirementDetail.product.reference',
      },
    });
  }

  columns.push(
    {
      name: 'Assigned',
      key: 'assigned',
      editable: false,
      resizable: true,
      formatter: Formatters.NumericFormatter,
      options: {
        format: { decimals: product?.precision || 0 },
      },
    },
    {
      name: 'Line Quantity',
      key: `quantity`, //Mock key - Real key access via options.keyFunction.
      editable: false,
      resizable: true,
      formatter: FunctionKeyFormatter,
      options: {
        formatter: NumericFormatter,
        formatterOptions: {
          format: { decimals: product?.precision || 0 },
        },
        keyFunction: (rowData: ISalesOrderLineAssignment) =>
          rowData.assignmentType === AssignmentType.PurchaseOrder
            ? `${lineKey}.quantity`
            : 'purchaseRequirementDetail.requiredQuantity',
      },
    },
    {
      name: 'Direct Delivery',
      key: 'directDelivery',
      editable: false,
      resizable: true,
      formatter: Formatters.BooleanFormatter,
    }
  );
  return columns;
};
